import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { all, spread } from 'axios';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import { Container, Row, Col, Form, Label } from 'reactstrap';
import Select from 'react-select';
import { orderBy, kebabCase } from 'lodash';
import useQuery from '~/utils/query/hook';
import { authorizedPyInstance as axios } from '~/utils';
import { useFeatures } from '@cordata/buffet';

import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import bootstrapPlugin from '@fullcalendar/bootstrap';

const calendarPlugins = [
  dayGridPlugin,
  timeGridPlugin,
  listPlugin,
  interactionPlugin,
  bootstrapPlugin,
];

const mapEvents = events =>
  events.map(e => ({
    id: e.id,
    title: {
      'nav-list-view-appointment': e.attributes.appointmentTypeLabel,
      'nav-list-view-medical-event': e.attributes.medicalEventLabel,
      'nav-list-view-procedure': e.attributes.procedureLabel,
      'nav-list-view-surgery': e.attributes.procedureLabel,
      'nav-list-view-testing': e.attributes.testLabel,
    }[e.type],
    start: {
      'nav-list-view-appointment': e.attributes.eventDate,
      'nav-list-view-medical-event': e.attributes.eventDate,
      'nav-list-view-procedure': e.attributes.eventDate,
      'nav-list-view-surgery': e.attributes.eventDate,
      'nav-list-view-testing': e.attributes.eventDate,
    }[e.type],
    url: {
      'nav-list-view-appointment': `/patients/${e.attributes.patientUid}/appointments/${e.id}/details`,
      'nav-list-view-medical-event': `/patients/${e.attributes.patientUid}/medical-events/${e.attributes.medicalEventUid}/${e.id}/details`,
      'nav-list-view-procedure': `/patients/${e.attributes.patientUid}/procedures/${e.id}/details`,
      'nav-list-view-surgery': `/patients/${e.attributes.patientUid}/surgeries/${e.id}/details`,
      'nav-list-view-testing': `/patients/${e.attributes.patientUid}/tests/${e.id}/details`,
    }[e.type],
  }));

const HomeCalendar = () => {
  const query = useQuery();
  const history = useHistory();
  const calendarRef = useRef();
  const [currentView, setCurrentView] = useState();

  const [viewStartDate, setViewStartDate] = useState(startOfWeek(new Date()));
  const [viewEndDate, setViewEndDate] = useState(endOfWeek(new Date()));

  const allOption = { label: 'All', value: '*' };
  const [providerOptions, setProviderOptions] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(allOption);
  const [eventTypeOptions, setEventTypeOptions] = useState([]);
  const [selectedEventType, setSelectedEventType] = useState(allOption);
  const [facilityOptions, setFacilityOptions] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(allOption);

  const [
    appointment,
    medicalEvent,
    procedure,
    surgery,
    testing,
  ] = useFeatures();
  const [eventsForCalendar, setEventsForCalendar] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    const eventsICareAbout = [];

    if (appointment) eventsICareAbout.push('appointment');
    if (medicalEvent) eventsICareAbout.push('medical_event');
    if (procedure) eventsICareAbout.push('procedure');
    if (surgery) eventsICareAbout.push('surgery');
    if (testing) eventsICareAbout.push('testing');

    setEventsForCalendar(eventsICareAbout);

    all([
      axios.get('/api/data/public/summary-provider'),
      axios.get(`/api/data/reference/reference-event?event-type-name=${eventsICareAbout.join('&event-type-name=')}`),
      axios.get('/api/data/public/facility', { params: { 'is-in-selections': true } }),
    ])
      .then(spread((providers, eventTypes, facilities) => ([
        providers.data.data,
        eventTypes.data.data,
        facilities.data.data,
      ])))
      .then(spread((providers, eventTypes, facilities) => ([
        orderBy(providers, ['attributes.lastname', 'attributes.firstname']),
        orderBy(eventTypes, 'attributes.eventTypeLabel'),
        orderBy(facilities, 'attributes.facilityName'),
      ])))
      .then(spread((providers, eventTypes, facilities) => ([
        providers.map(p => ({
          label: p.attributes.displayName,
          value: p.id,
        })),
        eventTypes.map(e => ({
          label: e.attributes.eventTypeLabel,
          value: e.attributes.eventTypeName,
        })),
        facilities.map(f => ({
          label: f.attributes.facilityName,
          value: f.id,
        })),
      ])))
      .then(spread((providers, eventTypes, facilities) => {
        setProviderOptions([
          allOption,
          ...providers,
        ]);

        setEventTypeOptions([
          allOption,
          ...eventTypes,
        ]);

        setFacilityOptions([
          allOption,
          ...facilities,
        ]);
      }));
  }, []);

  useEffect(() => {
    const startDate = format(viewStartDate);
    const endDate = format(viewEndDate);
    const eventsToGet = selectedEventType.value === '*' ? eventsForCalendar : [selectedEventType.value];

    const queryString = (e) => {
      const qs = [
        `event_date__>>=${startDate}`,
        `event_date__<<=${endDate}`,
      ];

      if (selectedProvider.value !== '*') qs.push(`provider_uid=${selectedProvider.value}`);
      if (selectedFacility.value !== '*') qs.push(`facility_uid=${selectedFacility.value}`);

      if (e !== 'medical_event') {
        ['scheduled', 'requested', 'pending pre-auth', 'completed'].forEach(statusName =>
          qs.push(`status_name=${statusName}`));
      }

      return encodeURI(qs.join('&'));
    };

    const eventQueryPromises = eventsToGet.map(e =>
      axios.get(`/api/data/public/nav-list-view-${kebabCase(e)}?${queryString(e)}`));

    all(eventQueryPromises)
      .then(spread((...eventResponses) => eventResponses.map(e => e.data.data).flat()))
      .then(spread((...eventDatas) => setEvents(mapEvents(eventDatas))));
  }, [
    eventsForCalendar,
    viewStartDate,
    viewEndDate,
    selectedProvider,
    selectedEventType,
    selectedFacility,
  ]);

  const onViewChange = (info) => {
    setCurrentView(info.view);
  };

  const onDateClick = (date) => {
    if (currentView.type !== 'timeGridDay') {
      calendarRef.current.getApi().changeView('timeGridDay', date.date);
    }
  };

  const onEventClick = (info) => {
    info.jsEvent.preventDefault();
    history.push(info.event.url);
  };

  const onDatesSet = (dates) => {
    setViewStartDate(dates.start);
    setViewEndDate(dates.end);
  };

  const customSelectStyles = {
    menu: provided => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  return (
    <Container fluid>
      <Form className="mb-3">
        <Row form>
          <Col md>
            <Label for="provider" className="text-primary">
              Provider
            </Label>

            <Select
              styles={customSelectStyles}
              name="provider"
              type="select"
              options={providerOptions}
              value={selectedProvider}
              onChange={setSelectedProvider}
            />
          </Col>

          <Col md>
            <Label for="eventType" className="text-primary">
              Event Type
            </Label>

            <Select
              styles={customSelectStyles}
              name="eventType"
              type="select"
              options={eventTypeOptions}
              value={selectedEventType}
              onChange={setSelectedEventType}
            />
          </Col>

          <Col md>
            <Label for="facility" className="text-primary">
              Facility
            </Label>

            <Select
              styles={customSelectStyles}
              name="facility"
              type="select"
              options={facilityOptions}
              value={selectedFacility}
              onChange={setSelectedFacility}
            />
          </Col>
        </Row>
      </Form>

      <FullCalendar
        ref={calendarRef}
        initialDate={query.startDate}
        initialView="dayGridMonth"
        plugins={calendarPlugins}
        themeSystem="bootstrap"
        bootstrapFontAwesome={false}
        headerToolbar={{
          left: 'prev,today,next',
          center: 'title',
          right: 'timeGridDay,timeGridWeek,dayGridMonth,listWeek',
        }}
        events={events}
        datesSet={onDatesSet}
        dateClick={onDateClick}
        eventClick={onEventClick}
        viewClassNames={onViewChange}
        /* viewClassNames is the only callback called each time the view changes */
        nowIndicator
      />
    </Container>
  );
};

export default HomeCalendar;
